import Cookies from 'js-cookie';
import { Controller } from 'stimulus';
import events from 'lib/events';
import {
  getFormattedPageValue, triggerCustomEvent, addLoadingOverlay, featureEnabled, splitTestEnabled,
} from 'lib/helpers';
import SearchStateManager from 'lib/search-state';

export default class SearchController extends Controller {
  connect() {
    this.searchState = new SearchStateManager();

    this.searchFormInput = document.querySelector('[data-target="search-form.input"]');

    if (this.searchFormInput) {
      this.contentType = this.searchFormInput.getAttribute('data-content-type');
    }

    if (featureEnabled('search-grid-reveal')) {
      addLoadingOverlay(document.querySelector('.search-results'), true);
    }

    window.addEventListener('PostSearchAjax', this.postAjaxEvent);
    window.addEventListener('SearchPaginationChange', this.searchPaginationEvent);
    window.addEventListener('Searched', this.performSearch);
    window.addEventListener('click', this.trackSearchClick);
    window.addEventListener('click', this.trackSponsoredSearchClick);
    window.addEventListener(events.search.apply_filters, this.applySearchFilters);
    window.addEventListener(events.search.update_content_type, this.resetContentType);
  }

  disconnect() {
    window.removeEventListener('PostSearchAjax', this.postAjaxEvent);
    window.removeEventListener('SearchPaginationChange', this.searchPaginationEvent);
    window.removeEventListener('Searched', this.performSearch);
    window.removeEventListener('click', this.trackSearchClick);
    window.removeEventListener('click', this.trackSponsoredSearchClick);
    window.removeEventListener(events.search.apply_filters, this.applySearchFilters);
    window.removeEventListener(events.search.update_content_type, this.resetContentType);
  }

  applySearchFilters = (event) => {
    const queryString = this.searchState.getFlatQueryString();
    const term = this.normalizeTerm(this.searchFormInput.value);
    const { searchRoute, verifiedSearchRoute } = this.searchFormInput.dataset;
    const { protocol, host } = window.location;

    this.resetContentType(event);

    const newSearchUrl = this.getFilteredSearchUrl(searchRoute, term, queryString, protocol, host, verifiedSearchRoute);

    Cookies.set('ezSearchType', 'internal');

    if (splitTestEnabled('vertical-grid-infinite-scroll')) {
      const filterEl = document.querySelector('.search-sidebar__sticky-wrapper__inner');
      if (filterEl) window.EezyStickyScrollPositionHelper.preservePositionOnce(filterEl);
    } else {
      window.EezyScrollPositionHelper.preservePositionOnce();
    }

    Turbolinks.visit(newSearchUrl);
  }

  postAjaxEvent = (event) => {
    this.postAjax(event.detail.dataHash);
  }

  searchPaginationEvent = (event) => {
    triggerCustomEvent('PaginationChanged', {
      page: event.detail.page,
      stateKey: 'search-state',
      extraStates: ['filterState'],
      ajaxEventName: 'PostSearchAjax',
      resultsTarget: document.querySelector('.search-results'),
    });
  }

  performSearch = (event) => {
    const { searchPath } = event.detail;
    const searchRoute = event.detail.searchRoute ? event.detail.searchRoute : this.searchRoute;
    const term = this.normalizeTerm(event.detail.term);

    let queryString;

    // If search contains new filter params
    if (event.detail.useSearchState) {
      queryString = this.searchState.getFlatQueryString();
    }

    this.resetContentType(event);

    const newSearchUrl = this.getNewSearchUrl(searchRoute, term, queryString, searchPath);

    if (event.detail.searchInNewTab === true) {
      window.open(newSearchUrl, '_blank');
    } else {
      Turbolinks.visit(newSearchUrl);
    }
  }

  resetContentType = (event) => {
    this.contentType = event.detail.contentType || this.contentType;

    if (event.detail.contentType) {
      Cookies.set('content_type', event.detail.contentType);
    }

    if (this.changingContentType()) {
      this.searchState.resetStateToDefault();
      triggerCustomEvent('search:reset_hidden_filters');
    }
  }

  changingContentType() {
    return this.contentType !== this.searchFormInput.getAttribute('data-content-type');
  }

  toggleSearchFilters() {
    const toggleSearchFiltersEvent = new CustomEvent('search:toggle_search_filters');
    window.dispatchEvent(toggleSearchFiltersEvent);
  }

  postAjax = (dataHash) => {
    this.searchState.updateStateValue('page', dataHash.page);
    const queryParams = this.searchState.getFlatQueryString();
    const { protocol, host, pathname } = window.location;

    if (this.searchType) {
      Cookies.set('ezSearchType', this.searchType);
    }

    if (this.usingPhrasebase && this.usingUnverifiedPath) {
      const newSearchUrl = this.makeUnverifiedSearchUrl(pathname,
        this.searchTerm,
        queryParams,
        protocol, host, false);

      window.location.assign(newSearchUrl);
    } else {
      window.location.assign(pathname + queryParams);
    }
  };

  makeUnverifiedSearchUrl(searchRoute, term, queryString, protocol, host) {
    return this.makeSearchPath(`${searchRoute}?qterm=${term}&content_type=${this.contentType}`,
      queryString, protocol, host, false);
  }

  makeVerifiedSearchUrl(searchRoute, term, queryString, protocol, host) {
    return this.makeSearchPath(`${searchRoute}/${term}`, queryString, protocol, host, true);
  }

  makeUrlFromSearchPath(searchPath) {
    return this.makeSearchPath(null, null, null, null, true, searchPath);
  }

  makeSearchPath(searchPath, queryString = null, protocol = null, host = null, verified = true, fullSearchPath = null) {
    if (fullSearchPath) return fullSearchPath;

    if (searchPath.indexOf('/') === 0) {
      searchPath = searchPath.replace(/^\/+/g, ''); /* eslint-disable-line no-param-reassign */
    }

    let url = `/${searchPath}`;

    if (protocol && host) url = `${protocol}//${host}/${searchPath}`;

    if (queryString && !this.changingContentType()) {
      if (verified) {
        url += queryString;
      } else {
        url += `&${queryString.replace(/^\?/, '')}`;
      }
    }

    return url;
  }

  getFilteredSearchUrl(searchRoute, term, queryString, protocol = null, host = null, verifiedSearchRoute = null) {
    let url;

    if (this.usingPhrasebase()) {
      if (this.usingUnverifiedPath) {
        url = this.makeUnverifiedSearchUrl(searchRoute, term, queryString, protocol, host);
      } else {
        const thisSearchRoute = verifiedSearchRoute || searchRoute;

        url = this.makeVerifiedSearchUrl(thisSearchRoute, term, queryString, protocol, host);
      }
    } else if (this.changingContentType() || this.contentType === 'photo') {
      url = this.makeUnverifiedSearchUrl('search', term, queryString, protocol, host);
    } else {
      url = this.makeVerifiedSearchUrl(searchRoute, term, queryString, protocol, host);
    }

    return url;
  }

  getNewSearchUrl(searchRoute, term, queryString, fullSearchPath = null) {
    let url;

    if (this.usingPhrasebase()) {
      if (fullSearchPath) {
        url = this.makeUrlFromSearchPath(fullSearchPath);
      } else {
        url = this.makeUnverifiedSearchUrl(searchRoute, term, queryString);
      }
    } else if (this.contentType === 'photo') {
      url = this.makeUnverifiedSearchUrl('search', term, queryString);
    } else {
      url = this.makeVerifiedSearchUrl(searchRoute, term, queryString);
    }

    return url;
  }

  footerPagerChange(event) {
    event.preventDefault(); // eslint-disable-line no-restricted-globals

    const curPageValue = document.querySelector(
      '[data-target="ez-num-pager.currentPage"]',
    );
    const nextPageValue = parseInt(getFormattedPageValue(curPageValue), 10) + 1;

    triggerCustomEvent('SearchPaginationChange', { page: nextPageValue });
  }

  trackSearchClick = (event) => {
    const gridItem = event.target.closest('.search-results .ez-search-results .ez-resource-grid__item');
    if (gridItem) {
      triggerCustomEvent(events.search.click, {
        resourceId: gridItem.getAttribute('data-item-id'),
        resourcePosition: gridItem.getAttribute('data-position'),
        resourcePro: gridItem.getAttribute('data-pro'),
        searchTerm: this.searchTerm,
      });
    }
  }

  trackSponsoredSearchClick = (event) => {
    const sponsoredGridItem = event.target.closest('.search-results .ez-sponsored-results .ez-resource-grid__item');

    if (sponsoredGridItem) {
      triggerCustomEvent(events.search.sponsored_click, {
        resourceId: '',
        resourcePosition: '',
        resourcePro: '',
        searchTerm: this.searchTerm,
        sponsored: true,
      });
    }
  }

  normalizeTerm(term) {
    return encodeURIComponent(term.toLowerCase().trim().replace(/ /g, '-'));
  }

  usingPhrasebase() {
    if (this.contentType === 'photo') return false;

    const searchLocale = document.querySelector('[data-controller*="search-form"]').getAttribute('data-search-locale');

    if (searchLocale === 'en' && this.contentType === 'vector') return true;

    return this.element.getAttribute('data-search-phrasebase') === 'true';
  }

  get searchRoute() {
    if (this.searchFormInput) {
      return this.searchFormInput.dataset.searchRoute;
    }

    return 'search';
  }

  get searchTerm() {
    const searchInfo = this.element.querySelector('[data-search-term]');
    if (searchInfo) {
      return searchInfo.getAttribute('data-search-term');
    }

    return '';
  }

  get searchType() {
    const searchInfo = this.element.querySelector('[data-search-type]');
    if (searchInfo) {
      return searchInfo.getAttribute('data-search-type');
    }

    return null;
  }

  get usingUnverifiedPath() {
    if (this.contentType === 'photo') return true;

    const { searchParams } = (new URL(document.location));
    return searchParams.get('qterm');
  }
}
